import { useState, useEffect } from 'react';
import CheckIcon from '../../assets/CheckIcon';
import CrossIcon from '../../assets/CrossIcon';
import RedCrossIcon from '../../assets/icon/RedCrossIcon';
import useBgColorStore from '../../store/bgColor';
import { validateDateRange } from '../../utils/dateUtils';
import DateSelector from '../common/calendar/dateSelector';
import ReusableButton from '../common/Button/Button';

import { useForm } from 'react-hook-form';
import { IEquipmentComplianceUpdate } from '../../type/updaterCompliance';
import { toast } from 'react-toastify';
import complianceApi from '../../api/complianceApi';
import CrossIconDarkForm from '../../assets/icon/CrossIconForm';

interface ViewStaffComplianceFormProps {
  closeModal: () => void;
  name?: string;
  compliance?: string;
  category?: string;
  recurrency?: string;
  status?: string;
  complianceLink?: string;
  lastComplianceDateDisplay?: string;
  nextComplianceDate?: string;
  note?: string;
  complianceId?: number;
  equipmentId?: number;
  equipmentTypeId?: number;
  lastHours?: number;
  nextHours?: number;
  onSubmitSuccess?: () => void;
  onStaffChange?: () => void;
  lastKms?: number;
  nextKms?: number;
}

export default function VehicleAndTrailerForm({
  closeModal,
  name,
  compliance,
  category,
  recurrency,
  status,
  complianceLink,
  lastComplianceDateDisplay,
  nextComplianceDate,
  note = '',
  complianceId = 0,
  equipmentId = 0,
  equipmentTypeId = 0,
  lastHours = 0,
  nextHours = 0,
  onSubmitSuccess,
  onStaffChange,
  nextKms,
  lastKms,
}: ViewStaffComplianceFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<IEquipmentComplianceUpdate>({
    defaultValues: {
      complianceId,
      equipmentId,
      equipmentTypeId,
      note,
      lastComplianceDate: lastComplianceDateDisplay ?? new Date().toISOString(),
      nextComplianceDate: nextComplianceDate ?? new Date().toISOString(),
      lastHours,
      nextHours,
    },
  });

  const { bgColor } = useBgColorStore();

  const [editableNote, setEditableNote] = useState(note);
  const [selectedFromDate, setSelectedFromDate] = useState<Date>(
    lastComplianceDateDisplay
      ? new Date(lastComplianceDateDisplay)
      : new Date(),
  );
  const [selectedToDate, setSelectedToDate] = useState<Date>(
    nextComplianceDate ? new Date(nextComplianceDate) : new Date(),
  );

  useEffect(() => {
    setValue('lastComplianceDate', selectedFromDate.toISOString());
    setValue('nextComplianceDate', selectedToDate.toISOString());
    setValue('note', editableNote);
  }, [selectedFromDate, selectedToDate, editableNote, setValue]);

  const handleFromDateChange = (date: Date | null) => {
    if (date) {
      setSelectedFromDate(date);
      if (validateDateRange(date, selectedToDate)) {
        setSelectedToDate(date);
      }
    }
  };

  const handleToDateChange = (date: Date | null) => {
    if (date) {
      setSelectedToDate(date);
    }
  };

  const onSubmit = async (data: IEquipmentComplianceUpdate) => {
    try {
      const response = await complianceApi.updateComplianceDetails({
        ...data,
        lastComplianceDate: selectedFromDate.toISOString(),
        nextComplianceDate: selectedToDate.toISOString(),
        note: editableNote
      });

      console.log(response);
      toast.success('updated successfully');
      if (onSubmitSuccess) {
        onSubmitSuccess(); // ✅ Refresh parent
      }
      if (onStaffChange) {
        onStaffChange();
      }
      closeModal();
    } catch (error) {
      toast.warn('details is not updated');
    }
  };

  return (
    <div className="max-w-xl min-w-[500px] mx-auto bg-sidebar-background p-4 rounded-lg shadow-md relative">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-text-blue">
          Vehicle And Trailer Compliance
        </h2>
        <button
          className="text-textColorGray hover:text-textColorGray p-2"
          onClick={closeModal}
        >
          <RedCrossIcon />
        </button>
      </div>

      <div className="border-t-2 rounded-b-md border-light-blue" />

      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="overflow-y-auto max-h-[calc(100vh-100px)] min-w-full grid grid-cols-2 gap-6">
          <div className="flex flex-col text-start">
            <p className="font-medium text-text-primary">Name</p>
            <p className="text-text-black">{name ?? '-'}</p>
          </div>

          <div className="flex flex-col text-start">
            <p className="font-medium text-text-primary">Compliance</p>
            <p className="text-text-black">{compliance ?? '-'}</p>
          </div>

          <div className="flex flex-col text-start">
            <p className="font-medium text-text-primary">Category</p>
            <p className="text-text-black">{category ?? '-'}</p>
          </div>

          <div className="flex flex-col text-start">
            <p className="font-medium text-text-primary">Recurrency</p>
            <p className="text-text-black">{recurrency ?? '-'}</p>
          </div>

          <div className="flex flex-col text-start">
            <p className="font-medium text-text-primary">Status</p>
            <p className="text-text-black">{status ?? '-'}</p>
          </div>
        </div>

        <div className="pt-4">
          <label className="block text-sm font-medium text-start text-text-primary">
            Note
          </label>
          <textarea
            rows={3}
            placeholder="Write here..."
            className="mt-1 block w-full p-2 border border-customBorder-border rounded-md bg-backgroundColor-darkThemeColor text-text-black"
            value={editableNote}
            onChange={(e) => setEditableNote(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2 mt-2">
          <div className="flex flex-col w-auto">
            <label className="font-medium text-text-primary mb-1 text-start">
              Last Done
            </label>
            <div className="flex items-center h-[44px] p-2 rounded border border-customBorder-border w-full bg-backgroundColor-darkThemeColor">
              <DateSelector
                onDateChange={handleFromDateChange}
                initialDate={selectedFromDate}
              />
            </div>
          </div>

          <div className="flex flex-col w-auto">
            <label className="font-medium text-text-primary mb-1 text-start">
              New Expiry
            </label>
            <div className="flex items-center h-[44px] p-2 rounded border border-customBorder-border w-full bg-backgroundColor-darkThemeColor">
              <DateSelector
                onDateChange={handleToDateChange}
                initialDate={selectedToDate}
              />
            </div>
          </div>
        </div>

        {complianceLink && (
          <div className="pt-4 mt-2 w-full break-words">
            <label className="block text-sm font-medium text-text-primary text-start">
              Compliance Records
            </label>
            <div className="mt-1 text-start w-full">
              <a
                href={complianceLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue underline text-md block break-all"
              >
                {complianceLink}
              </a>
            </div>
          </div>
        )}

        <div className="flex w-full justify-around mt-4">
          <ReusableButton
            variant="secondary"
            children={'Cancel'}
            icon={
              <div>
                <div className="block dark:hidden">
                  <CrossIcon />
                </div>
                <div className="hidden dark:block">
                  <CrossIconDarkForm />
                </div>
              </div>
            }
            onClick={closeModal}
            className="bg-button-cancelButtonBgColor ml-2 mt-2 w-[40%] text-text-cancelButtonTextColor"
          />
          <ReusableButton
            icon={<CheckIcon />}
            children={'Update'}
            variant="primary"
            className="bg-background-backgroundColor mr-2 mt-2 w-[40%]"
          />
        </div>
      </form>
    </div>
  );
}
